// src/components/PetAdoptionPage.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPets, getFeaturedPets } from '../services/petService';
import '../styles/PetAdoptionPage.css';

const placeholderImage = 'https://via.placeholder.com/800x600?text=Pet+Image';

const PetAdoptionPage: React.FC = () => {
  const [filter, setFilter] = useState<'all' | 'small' | 'medium' | 'large'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const petsPerPage = 8;

  const [pets, setPets] = useState<any[]>([]);
  const [featuredPets, setFeaturedPets] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Fetch data (assumes getPets/getFeaturedPets are synchronous or return data)
  useEffect(() => {
    try {
      setLoading(true);
      setError(null);

      const featured = getFeaturedPets();
      setFeaturedPets(Array.isArray(featured) ? featured : []);

      const { pets: fetchedPets, totalPages: pages } = getPets(
        currentPage,
        petsPerPage,
        filter,
        searchTerm
      );

      setPets(Array.isArray(fetchedPets) ? fetchedPets : []);
      setTotalPages(typeof pages === 'number' ? pages : 0);
      setLoading(false);
    } catch (err) {
      setError('Failed to load pets. Please try again later.');
      setLoading(false);
    }
  }, [filter, searchTerm, currentPage]);

  // Carousel auto-slide
  useEffect(() => {
    if (featuredPets.length > 0) {
      const iv = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % featuredPets.length);
      }, 5000);
      return () => clearInterval(iv);
    }
  }, [featuredPets]);

  const handlePageChange = (page: number) => {
    if (page < 1) return;
    if (totalPages && page > totalPages) return;
    setCurrentPage(page);
    document.getElementById('pet-list-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSearch = () => {
    setCurrentPage(1);
  };

  // Get a usable image url (handles empty arrays / missing properties)
  const getFirstImage = (mediaArray?: any[]) => {
    if (!Array.isArray(mediaArray) || mediaArray.length === 0) return placeholderImage;
    const firstMedia = mediaArray[0];
    if (!firstMedia) return placeholderImage;

    // If structured with url or image field
    const url = firstMedia.url || firstMedia.image || firstMedia.imageUrl || firstMedia.src;
    if (url && typeof url === 'string' && url.trim()) return url;

    // fallback to thumbnail or placeholder
    if (firstMedia.thumbnail && typeof firstMedia.thumbnail === 'string') return firstMedia.thumbnail;
    return placeholderImage;
  };

  return (
    <div className="pet-adoption-page">
      {/* Hero */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-text">
            <h1>Find Your Furry Soulmate</h1>
            <p className="subtitle">Open your heart and home to a pet in need. Adoption saves lives!</p>
            <div className="cta-buttons">
              <Link to="/pets" className="cta-button primary">Browse Pets</Link>
              <Link to="/about" className="cta-button secondary">About Adoption</Link>
            </div>
          </div>
          <div className="hero-image-container">
            <div className="pet-paws">
              <div className="paw"></div>
              <div className="paw"></div>
              <div className="paw"></div>
            </div>
          </div>
        </div>
        <div className="hero-wave">
          <svg viewBox="0 0 1440 120" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 32l48 16c48 16 144 48 240 48s192-32 288-42.7c96-10.3 192-16.3 288-5.3s192 37 288 48c96 11 192 5 240 0l48-5v32H0z" fill="#fff"/>
          </svg>
        </div>
      </section>

      {/* Featured Pets Carousel */}
      {featuredPets.length > 0 && (
        <section className="featured-pets" aria-label="Featured pets carousel">
          <h2>Featured Pets</h2>
          <div className="carousel-container">
            <div
              className="carousel-track"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {featuredPets.map((pet: any) => (
                <div key={pet.id} className="carousel-slide" role="group" aria-roledescription="slide">
                  <div className="slide-content">
                    <div className="pet-image">
                      <img
                        src={getFirstImage(pet.images)}
                        alt={pet.name || 'Featured pet'}
                        onError={e => (e.currentTarget.src = placeholderImage)}
                      />
                    </div>
                    <div className="pet-info">
                      <h3>{pet.name}</h3>
                      {pet.color && <p className="pet-color">{pet.color}</p>}
                      <p>{[pet.breed, pet.age].filter(Boolean).join(' • ')}</p>
                      <div className="carousel-buttons">
                        <Link to={`/pets/${pet.id}`} className="view-details-btn">View Details</Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              className="carousel-nav prev"
              aria-label="Previous slide"
              onClick={() => setCurrentSlide(prev => (prev === 0 ? featuredPets.length - 1 : prev - 1))}
            >
              ‹
            </button>
            <button
              className="carousel-nav next"
              aria-label="Next slide"
              onClick={() => setCurrentSlide(prev => (prev === featuredPets.length - 1 ? 0 : prev + 1))}
            >
              ›
            </button>

            <div className="carousel-dots" role="tablist" aria-label="Carousel dots">
              {featuredPets.map((_, index) => (
                <button
                  key={index}
                  className={`dot ${currentSlide === index ? 'active' : ''}`}
                  aria-label={`Go to slide ${index + 1}`}
                  onClick={() => setCurrentSlide(index)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Filters & Search */}
      <section className="pet-filters" aria-labelledby="filters-heading">
        <div className="filters-header">
          <h2 id="filters-heading">Find Your Perfect Companion</h2>
          <div className="search-container" role="search">
            <input
              type="text"
              placeholder="Search by name, breed, color or location..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleSearch()}
              aria-label="Search pets"
            />
            <button className="search-btn" onClick={handleSearch} aria-label="Search">🔍 Search</button>
          </div>
        </div>

        <div className="filter-controls">
          <div className="filter-category">
            <h3>Filter by Size:</h3>
            <div className="filter-buttons" role="tablist" aria-label="Size filters">
              {(['all', 'small', 'medium', 'large'] as const).map(sz => (
                <button
                  key={sz}
                  className={filter === sz ? 'active' : ''}
                  onClick={() => { setFilter(sz); setCurrentPage(1); }}
                  aria-pressed={filter === sz}
                >
                  {sz === 'all' ? 'All Sizes' : sz.charAt(0).toUpperCase() + sz.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pet List */}
      <section id="pet-list-section" className="pet-list" aria-live="polite">
        {error ? (
          <div className="error" role="alert">
            {error}
            <button onClick={() => window.location.reload()} className="retry-btn">Retry</button>
          </div>
        ) : loading ? (
          <div className="loading">Loading pets...</div>
        ) : pets.length === 0 ? (
          <div className="no-pets">No pets found matching your criteria</div>
        ) : (
          <div className="pets-grid">
            {pets.map((pet: any) => (
              <div key={pet.id} className="pet-card" tabIndex={0}>
                <div className="pet-image-container">
                  <img
                    src={getFirstImage(pet.images)}
                    alt={pet.name || 'pet'}
                    className="pet-image"
                    onError={e => (e.currentTarget.src = placeholderImage)}
                  />
                  <div className="pet-badge">{pet.size}</div>
                </div>

                <div className="pet-details">
                  <div className="pet-header">
                    <h2>{pet.name}</h2>
                    <span className="gender-badge">{pet.gender}</span>
                  </div>

                  <div className="pet-meta">
                    <p><strong>Breed:</strong> {pet.breed}</p>
                    <p><strong>Color:</strong> {pet.color}</p>
                    <p><strong>Age:</strong> {pet.age}</p>
                    <p><strong>Location:</strong> {pet.location}</p>
                  </div>

                  <p className="description">{(pet.description?.slice(0, 120) ?? '')}{pet.description ? '...' : ''}</p>

                  <div className="pet-health">
                    <div className="health-tag">{pet.vaccinated ? '✅ Vaccinated' : '❌ Not Vaccinated'}</div>
                    <div className="health-tag">{pet.neutered ? '✅ Neutered/Spayed' : '❌ Not Neutered/Spayed'}</div>
                  </div>

                  <div className="pet-footer">
                    <div className="adoption-fee">Adoption Fee: ${pet.adoptionFee ?? '0'}</div>
                    <Link to={`/pets/${pet.id}`} className="adopt-button">Adopt {pet.name}</Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Simple Prev/Next Pagination */}
      {!error && totalPages > 1 && (
        <div className="pagination-container" role="navigation" aria-label="Pagination">
          <button
            className="pagination-arrow"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            ‹ Previous
          </button>

          <div className="page-indicator">Page {currentPage} of {totalPages}</div>

          <button
            className="pagination-arrow"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            Next ›
          </button>
        </div>
      )}
    </div>
  );
};

export default PetAdoptionPage;
